import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { incidentManagementAPI } from '@/shared/lib/api';
import type {
  WarningDocument,
  PaymentAgreement,
  ResetRequest,
  EligibleEmployee,
  AgreementSituation,
  CreateAgreementFormData,
  ReviewResetRequestFormData,
  PaginatedResponse,
} from '../types';

// ==========================================
// WARNING DOCUMENTS
// ==========================================

export function useWarningDocument(id: string | undefined) {
  return useQuery<WarningDocument>({
    queryKey: ['incident-management', 'warning-documents', id],
    queryFn: async () => {
      if (!id) throw new Error('Warning Document ID is required');
      const res = await incidentManagementAPI.getDocumentById(id);
      return res.data.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePendingDocuments(employeeId: string | undefined) {
  return useQuery<WarningDocument[]>({
    queryKey: ['incident-management', 'pending-documents', employeeId],
    queryFn: async () => {
      if (!employeeId) throw new Error('Employee ID is required');
      const res = await incidentManagementAPI.getPendingDocuments(employeeId);
      return res.data.data ?? [];
    },
    enabled: !!employeeId,
    staleTime: 2 * 60 * 1000,
  });
}

export function useSignDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const res = await incidentManagementAPI.signDocument(id);
      return res.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'warning-documents', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'pending-documents'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'incidents'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'employee-history'] });
    },
  });
}

export function useUpdatePdfUrl() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, pdfUrl }: { id: string; pdfUrl: string }) => {
      const res = await incidentManagementAPI.updatePdfUrl(id, pdfUrl);
      return res.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'warning-documents', variables.id] });
    },
  });
}

// ==========================================
// PAYMENT AGREEMENTS
// ==========================================

export function usePaymentAgreement(id: string | undefined) {
  return useQuery<PaymentAgreement>({
    queryKey: ['incident-management', 'payment-agreements', id],
    queryFn: async () => {
      if (!id) throw new Error('Payment Agreement ID is required');
      const res = await incidentManagementAPI.getAgreementById(id);
      return res.data.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useActiveAgreementsByEmployee(employeeId: string | undefined) {
  return useQuery<PaymentAgreement[]>({
    queryKey: ['incident-management', 'active-agreements', employeeId],
    queryFn: async () => {
      if (!employeeId) throw new Error('Employee ID is required');
      const res = await incidentManagementAPI.getActiveAgreementsByEmployee(employeeId);
      return res.data.data ?? [];
    },
    enabled: !!employeeId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAllActiveAgreements() {
  return useQuery<PaymentAgreement[]>({
    queryKey: ['incident-management', 'all-active-agreements'],
    queryFn: async () => {
      const res = await incidentManagementAPI.getAllActiveAgreements();
      return res.data.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAllPendingAgreements() {
  return useQuery<PaymentAgreement[]>({
    queryKey: ['incident-management', 'all-pending-agreements'],
    queryFn: async () => {
      const res = await incidentManagementAPI.getAllPendingAgreements();
      return res.data.data ?? [];
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useAgreementSituation(id: string | undefined) {
  return useQuery<AgreementSituation>({
    queryKey: ['incident-management', 'agreement-situation', id],
    queryFn: async () => {
      if (!id) throw new Error('Payment Agreement ID is required');
      const res = await incidentManagementAPI.getAgreementSituation(id);
      return res.data.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateAgreement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateAgreementFormData) => {
      const res = await incidentManagementAPI.createAgreement(data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'payment-agreements'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'active-agreements'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'all-active-agreements'] });
    },
  });
}

export function useUpdateAgreement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { paidInstallments?: number; notes?: string } }) => {
      const res = await incidentManagementAPI.updateAgreement(id, data);
      return res.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'payment-agreements', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'active-agreements'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'all-active-agreements'] });
    },
  });
}

export function useRegisterPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await incidentManagementAPI.registerPayment(id);
      return res.data.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'payment-agreements', id] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'active-agreements'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'all-active-agreements'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'agreement-situation', id] });
    },
  });
}

export function useCancelAgreement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const res = await incidentManagementAPI.cancelAgreement(id, reason);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'payment-agreements'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'active-agreements'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'all-active-agreements'] });
    },
  });
}

export function useActivatePendingAgreement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: {
      id: string;
      data: { installments: number; startDate: string; notes?: string }
    }) => {
      const res = await incidentManagementAPI.activatePendingAgreement(id, data);
      return res.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'payment-agreements', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'all-pending-agreements'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'all-active-agreements'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'incidents'] });
    },
  });
}

// ==========================================
// RESET REQUESTS
// ==========================================

export function useResetRequests(params?: {
  page?: number;
  limit?: number;
  employeeId?: string;
  status?: string;
}) {
  return useQuery<PaginatedResponse<ResetRequest>>({
    queryKey: ['incident-management', 'reset-requests', params],
    queryFn: async () => {
      const res = await incidentManagementAPI.listResetRequests(params);
      return res.data.data ?? { data: [], pagination: { page: 1, limit: 10, total: 0, totalPages: 0 } };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useResetRequest(id: string | undefined) {
  return useQuery<ResetRequest>({
    queryKey: ['incident-management', 'reset-requests', id],
    queryFn: async () => {
      if (!id) throw new Error('Reset Request ID is required');
      const res = await incidentManagementAPI.getResetRequestById(id);
      return res.data.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useEligibleEmployees() {
  return useQuery<EligibleEmployee[]>({
    queryKey: ['incident-management', 'eligible-employees'],
    queryFn: async () => {
      const res = await incidentManagementAPI.getEligibleEmployees();
      return res.data.data ?? [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useCreateResetRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (employeeId: string) => {
      const res = await incidentManagementAPI.createResetRequest(employeeId);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'reset-requests'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'eligible-employees'] });
    },
  });
}

export function useReviewResetRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ReviewResetRequestFormData }) => {
      const res = await incidentManagementAPI.reviewResetRequest(id, data);
      return res.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'reset-requests'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'reset-requests', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'employee-history'] });
    },
  });
}
